import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router";
import { FaClipboardList, FaUser, FaCalendarAlt, FaBoxOpen, FaLocationArrow } from "react-icons/fa";
import { motion } from "framer-motion";
import styles from "./RedemptionOrderDetails.module.css";
import AuthContext from "../../Store/AuthContext";
import { BASE_URL_API } from "../../References/URLs";

const RedemptionOrderDetails = () => {
    const { orderIdentifier } = useParams();
    const [orderDetails, setOrderDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const authCtx = useContext(AuthContext)

    useEffect(() => {
        const fetchOrderDetails = async () => {
            try {
                const response = await fetch(`${BASE_URL_API}getRedemptionOrderDetails`, {
                    method: "POST",
                    body: JSON.stringify({
                        token: authCtx.token,
                        orderIdentifier
                    }),
                });

                if (!response.ok) throw new Error("Failed to fetch order details");

                const data = await response.json();
                if (data.status === "success") {
                    setOrderDetails(data.response);
                } else {
                    if (data?.message === "token Expired, please login again!") {
                        authCtx.logout()
                    } else {
                        console.error("Error:", data.message);
                    }
                }
            } catch (error) {
                console.error("Error fetching order:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchOrderDetails();
    }, [orderIdentifier]);

    function calculateTotal(items) {
        return items.reduce((total, item) => {
            return total + (parseInt(item.quantity) * parseInt(item.pointsUsed));
        }, 0);
    }

    if (loading) {
        return <div className={styles.loading}>Loading order details...</div>;
    }

    if (!orderDetails) {
        return <div className={styles.error}>Order not found</div>;
    }

    return (
        <motion.div
            className={styles.container}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
        >
            <div className={styles.mainContainer}>
                <div className={styles.leftContainer}>
                    <div className={styles.title}>
                        <FaClipboardList /> Order Details
                    </div>
                    <div className={styles.card}>
                        <motion.div
                            className={styles.detailItem}
                            whileHover={{ scale: 1.05 }}
                        >
                            <FaBoxOpen className={styles.icon} />
                            <strong>Order ID:</strong> {orderDetails.orderIdentifier}
                        </motion.div>

                        <div>
                            <motion.div
                                className={styles.detailItem}
                                whileHover={{ scale: 1.05 }}
                            >
                                <FaUser className={styles.icon} />
                                <strong>Customer Name:</strong> {orderDetails.firstName}, {orderDetails?.phoneNumber}
                            </motion.div>
                            <motion.div
                                className={styles.detailItem}
                                whileHover={{ scale: 1.05 }}
                            >
                                <FaLocationArrow className={styles.icon} />
                                <strong>Delivery Address:</strong> {orderDetails.deliveryAddress}
                            </motion.div>


                        </div>
                        <motion.div
                            className={styles.detailItem}
                            whileHover={{ scale: 1.05 }}
                        >
                            <FaCalendarAlt className={styles.icon} />
                            <strong>Order Date:</strong> {new Date(orderDetails.placedOn).toLocaleDateString()}
                        </motion.div>

                        <motion.div
                            className={styles.detailItem}
                            whileHover={{ scale: 1.05 }}
                        >
                            <strong>Status:</strong> <span className={styles.status}>{orderDetails.currentStatus}</span>
                        </motion.div>
                        <div>
                            {orderDetails?.orderItems?.map((itemOrder, indexOrder) => (
                                <div key={indexOrder} className={styles.itemOrderWrapper}>
                                    <div>
                                        <div className={styles.productTitle}>
                                            {itemOrder?.productTitle}
                                        </div>
                                        <div className={styles.priceQty}>
                                            <div>
                                                {itemOrder?.pointsUsed}
                                            </div>
                                            <div>x</div>
                                            <div>
                                                {itemOrder?.quantity}
                                            </div>
                                            <div>=</div>
                                            <div>{itemOrder?.pointsUsed * itemOrder?.quantity}</div>
                                        </div>
                                    </div>
                                    <div>
                                        <div className={styles.productThumbnail}>
                                            <img src={itemOrder?.productThumbnail} />
                                        </div>
                                    </div>
                                </div>
                            ))}

                        </div>
                    </div>

                </div>
                <div className={styles.rightContainer}>
                    <div>
                        <div>
                            <div className={styles.rcSectionTitle}>Shipping Details</div>
                            <div className={styles.rcSectionBody}>
                                <div className={styles.firstName}>
                                    {orderDetails?.firstName} {orderDetails?.lastName}
                                </div>
                                <div className={styles.fullAddress}>
                                    {orderDetails?.deliveryAddress}
                                </div>
                                <div className={styles.phoneNumber}>
                                    <div className={styles.phone}>Phone : </div>
                                    <div>
                                        {orderDetails?.phoneNumber}
                                    </div>
                                </div>

                            </div>
                        </div>

                        <div className={styles.eachSection}>
                            <div className={`${styles.rcSectionTitle} ${styles.priceDetailsTitle}`}>Price Details</div>
                            <div className={styles.rcSectionBody}>
                                {orderDetails?.orderItems?.map((itemInOrder, indexInOrder) => (
                                    <div key={indexInOrder} className={styles.eachItemInpd}>
                                        <div className={styles.toprowWrapper}>
                                            <div>
                                                {itemInOrder?.productTitle}
                                            </div>
                                            <div>
                                                <div>
                                                    {itemInOrder?.pointsUsed}
                                                </div>
                                            </div>
                                        </div>
                                        <div className={styles.bottomrowWrapper}>
                                            <div>
                                                {itemInOrder?.pointsUsed} x {itemInOrder?.quantity} = {itemInOrder?.pointsUsed * itemInOrder?.quantity}
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                <div className={styles.totalWrapper}>
                                    <div>
                                        Total
                                    </div>
                                    <div>
                                        {calculateTotal(orderDetails?.orderItems)}
                                    </div>
                                </div>

                                <div className={styles.paymentMode}>
                                    <div>
                                        Payment Mode :
                                    </div>
                                    <div>
                                        {orderDetails?.paymentMethod || "Points"}
                                    </div>
                                </div>

                            </div>
                        </div>

                    </div>

                </div>
            </div>

        </motion.div>
    );
};

export default RedemptionOrderDetails;
